import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

interface PARUser {
  user_id: string;
  display_name: string | null;
  email: string | null;
  created_at: string;
}

interface UploadActivity {
  user_id: string;
  last_upload: string | null;
  invoice_count: number;
}

interface ReminderSetting {
  user_id: string;
  enabled: boolean;
}

interface PARUserRow {
  user_id: string;
  display_name: string | null;
  email: string | null;
  created_at: string;
  last_upload: string | null;
  invoice_count: number;
  reminders_enabled: boolean | null;
}

function formatDate(iso: string | null): string {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

export function PARClientPanel() {
  const [rows, setRows] = useState<PARUserRow[]>([]);
  const [totalInvoices, setTotalInvoices] = useState<number>(0);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    async function fetchData() {
      setIsLoading(true);
      setError(null);

      try {
        // 1. PAR users from profiles
        const { data: profilesData, error: profilesError } = await supabase
          .from("profiles")
          .select("user_id, display_name, email, created_at")
          .eq("role" as never, "PAR");

        if (profilesError) throw profilesError;

        const parUsers: PARUser[] = (profilesData ?? []) as PARUser[];

        if (parUsers.length === 0) {
          setRows([]);
          setTotalInvoices(0);
          setIsLoading(false);
          return;
        }

        const parUserIds = parUsers.map((u) => u.user_id);

        // 2. Upload activity — last upload + count per user
        const { data: invoiceData, error: invoiceError } = await supabase
          .from("par_invoices")
          .select("user_id, created_at")
          .in("user_id", parUserIds);

        if (invoiceError) throw invoiceError;

        const uploadMap: Record<string, UploadActivity> = {};
        let total = 0;
        for (const row of invoiceData ?? []) {
          total++;
          if (!uploadMap[row.user_id]) {
            uploadMap[row.user_id] = {
              user_id: row.user_id,
              last_upload: row.created_at,
              invoice_count: 1,
            };
          } else {
            uploadMap[row.user_id].invoice_count++;
            if (row.created_at > (uploadMap[row.user_id].last_upload ?? "")) {
              uploadMap[row.user_id].last_upload = row.created_at;
            }
          }
        }
        setTotalInvoices(total);

        // 3. Reminder settings
        const { data: reminderData, error: reminderError } = await supabase
          .from("par_reminder_settings")
          .select("user_id, enabled")
          .in("user_id", parUserIds);

        if (reminderError) throw reminderError;

        const reminderMap: Record<string, ReminderSetting> = {};
        for (const row of (reminderData ?? []) as ReminderSetting[]) {
          reminderMap[row.user_id] = row;
        }

        // 4. Assemble rows
        const assembled: PARUserRow[] = parUsers.map((u) => ({
          user_id: u.user_id,
          display_name: u.display_name,
          email: u.email,
          created_at: u.created_at,
          last_upload: uploadMap[u.user_id]?.last_upload ?? null,
          invoice_count: uploadMap[u.user_id]?.invoice_count ?? 0,
          reminders_enabled: reminderMap[u.user_id]?.enabled ?? null,
        }));

        setRows(assembled);
      } catch (err) {
        const msg = err instanceof Error ? err.message : String(err);
        setError(msg);
      } finally {
        setIsLoading(false);
      }
    }

    void fetchData();
  }, []);

  return (
    <Card className="overflow-hidden">
      <div className="p-5 border-b border-border">
        <h2 className="text-base font-semibold font-display">PAR Client — PAR Systems Usage</h2>
        <p className="text-xs text-muted-foreground mt-0.5">
          Activity summary for all PAR-role users
        </p>
      </div>

      {/* Feature usage summary */}
      <div className="px-5 py-3 border-b border-border flex flex-wrap gap-6 text-sm">
        <div>
          <span className="text-muted-foreground text-xs">Total PAR users</span>
          <p className="font-semibold">{isLoading ? "…" : rows.length}</p>
        </div>
        <div>
          <span className="text-muted-foreground text-xs">Total invoices uploaded</span>
          <p className="font-semibold">{isLoading ? "…" : totalInvoices.toLocaleString()}</p>
        </div>
        <div>
          <span className="text-muted-foreground text-xs">Users with reminders on</span>
          <p className="font-semibold">
            {isLoading ? "…" : rows.filter((r) => r.reminders_enabled === true).length}
          </p>
        </div>
      </div>

      {isLoading ? (
        <div className="p-8 text-center text-muted-foreground text-sm">Loading…</div>
      ) : error ? (
        <div className="p-8 text-center text-destructive text-sm">{error}</div>
      ) : rows.length === 0 ? (
        <div className="p-8 text-center text-muted-foreground text-sm">No PAR users found.</div>
      ) : (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Name</TableHead>
              <TableHead>Email</TableHead>
              <TableHead>Member Since</TableHead>
              <TableHead>Last Upload</TableHead>
              <TableHead className="text-right">Invoices</TableHead>
              <TableHead>Reminders</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map((row) => (
              <TableRow key={row.user_id}>
                <TableCell className="font-medium">
                  {row.display_name ?? <span className="text-muted-foreground italic">—</span>}
                </TableCell>
                <TableCell className="text-muted-foreground">{row.email ?? "—"}</TableCell>
                <TableCell>{formatDate(row.created_at)}</TableCell>
                <TableCell>{formatDate(row.last_upload)}</TableCell>
                <TableCell className="text-right">{row.invoice_count.toLocaleString()}</TableCell>
                <TableCell>
                  {row.reminders_enabled === null ? (
                    <Badge variant="outline" className="text-[10px]">
                      Not set
                    </Badge>
                  ) : row.reminders_enabled ? (
                    <Badge className="text-[10px] bg-emerald-100 text-emerald-700 hover:bg-emerald-100">
                      On
                    </Badge>
                  ) : (
                    <Badge variant="secondary" className="text-[10px]">
                      Off
                    </Badge>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </Card>
  );
}
